/**
 * Action Toast Configuration
 *
 * Centralized configuration for toast messages shown during action execution.
 * Provides default messages for all actions with ability to override per-call.
 *
 * IMPORTANT: This map is typed as Record<Action, ...> (not Partial) intentionally.
 * Every Action enum value MUST have an entry here — TypeScript will error if one is
 * missing. Silent fetches use { enabled: false }; mutations get loading/success/error
 * strings. This enforces that new actions are never silently invisible to the user.
 */

import { Action } from '@/business/types/action.types';

/**
 * Toast messages for an action lifecycle
 */
export interface ActionToastMessages {
  /** Message shown when action starts (loading state) */
  loading?: string;
  /** Message shown on success */
  success?: string;
  /** Message shown on error (can be overridden by specific error code) */
  error?: string;
  /** Whether to show toasts for this action (default: true) */
  enabled?: boolean;
}

/**
 * Default toast messages for each action.
 *
 * Guidelines:
 * - Loading: Present continuous tense ("Saving...", "Loading...")
 * - Success: Past tense, positive ("Saved!", "Loaded successfully")
 * - Error: Keep generic, specific errors come from response codes
 * - Silent fetches / internal ops: { enabled: false }
 */
export const ACTION_TOAST_CONFIG: Record<Action, ActionToastMessages> = {
  // Auth actions
  [Action.LOGIN]: {
    loading: 'Signing in...',
    success: 'Welcome back!',
    error: 'Failed to sign in',
  },
  [Action.LOGOUT]: {
    loading: 'Signing out...',
    success: 'Signed out successfully',
    error: 'Failed to sign out',
  },

  // User actions
  [Action.CREATE_USER]: {
    loading: 'Creating user...',
    success: 'User created!',
    error: 'Failed to create user',
  },
  [Action.DELETE_USER]: {
    loading: 'Deleting user...',
    success: 'User deleted!',
    error: 'Failed to delete user',
  },
  [Action.GET_PREFERENCES]: { enabled: false }, // Silent background fetch
  [Action.GET_ORGANIZATION_USERS]: { enabled: false }, // Silent
  [Action.RESET_USER_PASSWORD]: {
    loading: 'Resetting password...',
    success: 'Password reset successfully',
    error: 'Failed to reset password',
  },
  [Action.UPDATE_USER]: {
    loading: 'Updating user...',
    success: 'User updated!',
    error: 'Failed to update user',
  },
  [Action.UPDATE_USER_PREFERENCES]: {
    loading: 'Saving preferences...',
    success: 'Preferences saved!',
    error: 'Failed to save preferences',
  },
  [Action.UPDATE_PROFILE_PICTURE]: {
    loading: 'Updating profile picture...',
    success: 'Profile picture updated!',
    error: 'Failed to update profile picture',
  },
  [Action.GET_USER_PROFILE]: { enabled: false }, // Silent fetch

  // Organization actions
  [Action.CREATE_ORGANIZATION]: {
    loading: 'Creating organization...',
    success: 'Organization created!',
    error: 'Failed to create organization',
  },
  [Action.DELETE_ORGANIZATION]: {
    loading: 'Deleting organization...',
    success: 'Organization deleted!',
    error: 'Failed to delete organization',
  },
  [Action.GET_ALL_ORGANIZATIONS]: { enabled: false }, // Silent
  [Action.GET_ORG_CONFIGURATIONS]: { enabled: false }, // Silent fetch
  [Action.RESTORE_ORGANIZATION]: {
    loading: 'Restoring organization...',
    success: 'Organization restored!',
    error: 'Failed to restore organization',
  },
  [Action.SWITCH_ORGANIZATION]: {
    loading: 'Switching organization...',
    success: 'Organization switched!',
    error: 'Failed to switch organization',
  },
  [Action.UPDATE_ORGANIZATION]: {
    loading: 'Updating organization...',
    success: 'Organization updated!',
    error: 'Failed to update organization',
  },
  [Action.UPDATE_ORG_CONFIGURATION]: {
    loading: 'Updating configuration...',
    success: 'Configuration updated!',
    error: 'Failed to update configuration',
  },
  [Action.UPDATE_ORG_PROFILE_PICTURE]: {
    loading: 'Updating organization picture...',
    success: 'Organization picture updated!',
    error: 'Failed to update organization picture',
  },
  [Action.LIST_ORG_PROFILE_PICTURES]: { enabled: false }, // Silent fetch

  // Storage actions
  [Action.GET_PRESIGNED_UPLOAD_URL]: { enabled: false }, // Internal — no user-visible feedback
  [Action.LIST_PROFILE_PICTURES]: { enabled: false }, // Silent fetch
  [Action.LIST_BOARD_BACKGROUNDS]: { enabled: false }, // Silent fetch

  // User group actions
  [Action.CREATE_USER_GROUP]: {
    loading: 'Creating group...',
    success: 'Group created!',
    error: 'Failed to create group',
  },
  [Action.DELETE_USER_GROUP]: {
    loading: 'Deleting group...',
    success: 'Group deleted!',
    error: 'Failed to delete group',
  },
  [Action.GET_USER_GROUPS]: { enabled: false }, // Silent fetch
  [Action.ADD_USER_GROUP_MEMBER]: {
    loading: 'Adding member...',
    success: 'Member added!',
    error: 'Failed to add member',
  },
  [Action.BULK_ADD_USER_GROUP_MEMBERS]: {
    loading: 'Adding members...',
    success: 'Members added!',
    error: 'Failed to add members',
  },
  [Action.REMOVE_USER_GROUP_MEMBER]: {
    loading: 'Removing member...',
    success: 'Member removed!',
    error: 'Failed to remove member',
  },
  [Action.GET_USER_GROUP_MEMBERS]: { enabled: false }, // Silent fetch
  [Action.GET_USER_GROUP_MEMBERS_BULK]: { enabled: false }, // Silent fetch

  // Board actions
  [Action.CREATE_BOARD]: {
    loading: 'Creating board...',
    success: 'Board created!',
    error: 'Failed to create board',
  },
  [Action.CREATE_BOARD_FROM_TEMPLATE]: {
    loading: 'Creating board...',
    success: 'Board created from template!',
    error: 'Failed to create board',
  },
  [Action.CREATE_BOARD_STATUS]: {
    loading: 'Adding column...',
    success: 'Column added!',
    error: 'Failed to add column',
  },
  [Action.CREATE_BOARD_TEMPLATE]: {
    loading: 'Creating template...',
    success: 'Template created!',
    error: 'Failed to create template',
  },
  [Action.CREATE_TASK]: {
    loading: 'Creating card...',
    success: 'Card created!',
    error: 'Failed to create card',
  },
  [Action.CREATE_TASK_RELATION]: { enabled: false }, // Internal linking op — silent
  [Action.CREATE_COMMENT]: {
    loading: 'Posting comment...',
    success: 'Comment posted!',
    error: 'Failed to post comment',
  },
  [Action.DELETE_BOARD]: {
    loading: 'Deleting board...',
    success: 'Board deleted!',
    error: 'Failed to delete board',
  },
  [Action.DELETE_BOARD_STATUS]: {
    loading: 'Deleting column...',
    success: 'Column deleted!',
    error: 'Failed to delete column',
  },
  [Action.DELETE_BOARD_TEMPLATE]: {
    loading: 'Deleting template...',
    success: 'Template deleted!',
    error: 'Failed to delete template',
  },
  [Action.DELETE_TASK]: {
    loading: 'Deleting card...',
    success: 'Card deleted!',
    error: 'Failed to delete card',
  },
  [Action.DELETE_TASK_RELATION]: { enabled: false }, // Internal unlinking op — silent
  [Action.GET_ALL_BOARDS]: { enabled: false }, // Silent
  [Action.GET_BOARD]: { enabled: false }, // Silent — no toast for data fetching
  [Action.GET_BOARD_PREFERENCES]: { enabled: false }, // Silent fetch
  [Action.GET_BOARD_TEMPLATES]: { enabled: false }, // Silent fetch
  [Action.GET_TASK]: { enabled: false }, // Silent
  [Action.GET_TASK_ASSIGNMENTS]: { enabled: false }, // Silent
  [Action.GET_TASK_RELATIONS]: { enabled: false }, // Silent fetch
  [Action.GET_COMMENTS]: { enabled: false }, // Silent
  [Action.RESTORE_BOARD]: {
    loading: 'Restoring board...',
    success: 'Board restored!',
    error: 'Failed to restore board',
  },
  [Action.RESTORE_BOARD_STATUS]: {
    loading: 'Restoring column...',
    success: 'Column restored!',
    error: 'Failed to restore column',
  },
  [Action.RESTORE_BOARD_TEMPLATE]: {
    loading: 'Restoring template...',
    success: 'Template restored!',
    error: 'Failed to restore template',
  },
  [Action.RESTORE_TASK]: {
    loading: 'Restoring card...',
    success: 'Card restored!',
    error: 'Failed to restore card',
  },
  [Action.SAVE_BOARD_AS_TEMPLATE]: {
    loading: 'Saving template...',
    success: 'Template saved!',
    error: 'Failed to save template',
  },
  [Action.SEARCH_USERS]: { enabled: false }, // Silent — autocomplete fetch
  [Action.UPDATE_BOARD]: {
    loading: 'Updating board...',
    success: 'Board updated!',
    error: 'Failed to update board',
  },
  [Action.UPDATE_BOARD_TEMPLATE]: {
    loading: 'Updating template...',
    success: 'Template updated!',
    error: 'Failed to update template',
  },
  [Action.UPDATE_BOARD_BACKGROUND]: {
    loading: 'Updating background...',
    success: 'Background updated!',
    error: 'Failed to update background',
  },
  [Action.UPDATE_BOARD_STATUS_CONFIG]: {
    loading: 'Saving column rules...',
    success: 'Column rules updated!',
    error: 'Failed to update column rules',
  },
  [Action.UPDATE_TASK]: {
    loading: 'Saving card...',
    success: 'Card saved!',
    error: 'Failed to save card',
  },
  [Action.UPDATE_TASK_STATUS]: {
    loading: 'Moving card...',
    success: 'Card moved!',
    error: 'Failed to move card',
  },

  // Dashboard actions
  [Action.GET_MY_PROGRESS]: { enabled: false }, // Silent fetch
  [Action.GET_DASHBOARD_STATS]: { enabled: false }, // Silent
  [Action.GET_LEARNING_PROGRESS_STATS]: { enabled: false }, // Silent fetch

  // Scheduled jobs actions
  [Action.GET_SCHEDULED_JOBS]: { enabled: false }, // Silent fetch
  [Action.CREATE_SCHEDULED_JOB]: {
    loading: 'Creating job...',
    success: 'Job created!',
    error: 'Failed to create job',
  },
  [Action.UPDATE_SCHEDULED_JOB]: {
    loading: 'Updating job...',
    success: 'Job updated!',
    error: 'Failed to update job',
  },
  [Action.RUN_SCHEDULED_JOB]: {
    loading: 'Starting job...',
    success: 'Job started!',
    error: 'Failed to start job',
  },
  [Action.GET_JOB_RUN]: { enabled: false }, // Silent fetch
  [Action.GET_JOB_RUNS]: { enabled: false }, // Silent fetch
  [Action.CANCEL_JOB_RUN]: {
    loading: 'Cancelling job...',
    success: 'Job cancelled!',
    error: 'Failed to cancel job',
  },

  // Cohort actions
  [Action.GET_COHORT_BOARD]: { enabled: false }, // Silent fetch
  [Action.GET_COHORT_PROGRESS]: { enabled: false }, // Silent fetch
  [Action.SYNC_COHORTS]: {
    loading: 'Syncing cohorts...',
    success: 'Cohorts synced!',
    error: 'Failed to sync cohorts',
  },

  // Notification actions
  [Action.GET_NOTIFICATIONS]: { enabled: false }, // Silent fetch
  [Action.MARK_NOTIFICATION_READ]: { enabled: false }, // Optimistic — no toast needed

  // Test actions
  [Action.NO_OP]: { enabled: false },
};

/**
 * Get toast configuration for an action
 * @param action The action to get config for
 * @returns Toast configuration with defaults applied
 */
export function getActionToastConfig(action: Action): ActionToastMessages {
  const config = ACTION_TOAST_CONFIG[action];

  // If explicitly disabled, return disabled config
  if (config?.enabled === false) {
    return { enabled: false };
  }

  // Return config with defaults
  return {
    loading: config?.loading,
    success: config?.success,
    error: config?.error,
    enabled: true, // Default to enabled
  };
}
